import * as rxjs from 'rxjs';

import * as Elevated from '@pitaman71/omniglot-introspect';
import * as LazyQueue from '../LazyQueue';
import * as Stores from '../Stores';
import { SequenceClient } from '../Clients';
import { SequenceStream } from '../Streams';
import { BaseMutation } from './BaseMutation';
import { OfScalar } from '../Mutations';
import { Streams } from '..';

export function Compare<ValueType>(as: Array<ValueType>, bs: Array<ValueType>): number {
    if (as.length < bs.length) return -1;
    if (as.length > bs.length) return +1;
    for (let index=0;index < as.length;index++) {
        if(as[index] < bs[index]) {
            return -1;
        } else if(as[index] > bs[index]) {
            return +1;
        }
    }
    return 0;
}

export function Equality<ValueType>(as: Array<ValueType>, bs: Array<ValueType>) {
    return Compare(as, bs) === 0;
}

export class Status<ValueType> extends Elevated.Serializable {
    status: Streams.Status = {};

    // implement required methods from Serializable
    static ClassSpec = 'omiglot-live-data.Mutations.OfSequence.Status';
    static Factory = Elevated.factories.concrete<Status<any>>(Status.ClassSpec, () => new Status<any>());
    getFactory = () => Status.Factory;
    getGlobalId(): number|string|null { return null; }
    marshal(visitor: Elevated.Visitor<this>): void {
        visitor.begin(this);
        visitor.end(this);
    }

    static from<ValueType>(status_: Streams.Status) {
        const result = new Status<ValueType>();
        result.status = status_;
        return result;
    }
}

export class Revert<ValueType> extends Elevated.Serializable {
    toMutation = new Elevated.Reference<Mutation<ValueType>>();

    // implement required methods from Serializable
    static ClassSpec = 'omiglot-live-data.Mutations.OfSequence.Revert';
    static Factory = Elevated.factories.concrete<Revert<any>>(Revert.ClassSpec, () => new Revert<any>());
    getFactory = () => Revert.Factory;
    getGlobalId(): number|string|null { return null; }
    marshal(visitor: Elevated.Visitor<this>): void {
        visitor.begin(this);
        visitor.reference(Mutation.Factory, 'toMutation');
        visitor.end(this);
    }

    toString(): string {
        const globalId = this.getGlobalId();
        return `${this.getFactory()} ${this.toMutation}`;
    }
    static from<ValueType>(obj: {
        toMutation: Elevated.Reference<Mutation<ValueType>>
    }) {
        const result = new Revert<ValueType>();
        result.toMutation = obj.toMutation
        return result;
    }
}

export class Clear<ValueType> extends Elevated.Serializable {
    // implement required methods from Serializable
    static ClassSpec = 'omiglot-live-data.Mutations.OfSequence.Clear';
    static Factory = Elevated.factories.concrete<Clear<any>>(Clear.ClassSpec, () => new Clear<any>());
    getFactory = () => Clear.Factory;
    getGlobalId(): number|string|null { return null; }
    marshal(visitor: Elevated.Visitor<this>): void {
        visitor.begin(this);
        visitor.end(this);
    }

    static from<ValueType>(obj: {
    }) {
        const result = new Clear<ValueType>();
        return result;
    }
}

export class Assign<ValueType> extends Elevated.Serializable {
    value: Array<ValueType> = [];

    // implement required methods from Serializable
    static ClassSpec = 'omiglot-live-data.Mutations.OfSequence.Assign';
    static Factory = Elevated.factories.concrete<Assign<any>>(Assign.ClassSpec, () => new Assign<any>());
    getFactory = () => Assign.Factory;
    getGlobalId(): number|string|null { return null; }
    marshal(visitor: Elevated.Visitor<this>): void {
        visitor.begin(this);
        visitor.primitive(this, 'value');
        visitor.end(this);
    }

    static from<ValueType>(obj: {
        value: Array<ValueType>
    }) {
        const result = new Assign<ValueType>();
        result.value = [ ... obj.value ];
        return result;
    }
}

export class Remove<ValueType> extends Elevated.Serializable {
    values: Set<ValueType> = new Set();

    // implement required methods from Serializable
    static ClassSpec = 'omiglot-live-data.Mutations.OfSequence.Remove';
    static Factory = Elevated.factories.concrete<Remove<any>>(Remove.ClassSpec, () => new Remove<any>());
    getFactory = () => Remove.Factory;
    getGlobalId(): number|string|null { return null; }
    marshal(visitor: Elevated.Visitor<this>): void {
        visitor.begin(this);
        visitor.primitive(this, 'values');
        visitor.end(this);
    }

    static from<ValueType>(obj: {
        values: Set<ValueType>
    }) {
        const result = new Remove<ValueType>();
        result.values = new Set(obj.values);
        return result;
    }
}

export class Append<ValueType> extends Elevated.Serializable {
    after: ValueType|undefined;
    values: ValueType[] = [];

    // implement required methods from Serializable
    static ClassSpec = 'omiglot-live-data.Mutations.OfSequence.Append';
    static Factory = Elevated.factories.concrete<Append<any>>(Append.ClassSpec, () => new Append<any>());
    getFactory = () => Append.Factory;
    getGlobalId(): number|string|null { return null; }
    marshal(visitor: Elevated.Visitor<this>): void {
        visitor.begin(this);
        visitor.primitive(this, 'after');
        visitor.primitive(this, 'values');
        visitor.end(this)
    }


    static from<ValueType>(obj: {
        after: ValueType|undefined;
        values: ValueType[];
    }) {
        const result = new Append<ValueType>();
        result.after = obj.after;
        result.values = [ ... obj.values ];
        return result;
    }

}

export class Prepend<ValueType> extends Elevated.Serializable {
    before: ValueType|undefined;
    values: ValueType[] = [];

    // implement required methods from Serializable
    static ClassSpec = 'omiglot-live-data.Mutations.OfSequence.Prepend';
    static Factory = Elevated.factories.concrete<Prepend<any>>(Prepend.ClassSpec, () => new Prepend<any>());
    getFactory = () => Prepend.Factory;
    getGlobalId(): number|string|null { return null; }
    marshal(visitor: Elevated.Visitor<this>): void {
        visitor.begin(this);
        visitor.primitive(this, 'before');
        visitor.primitive(this, 'values');
        visitor.end(this);
    }


    static from<ValueType>(obj: {
        before: ValueType|undefined;
        values: ValueType[];
    }) {
        const result = new Prepend<ValueType>();
        result.before = obj.before;
        result.values = [ ... obj.values ];
        return result;
    }
}
export class Mutation<ValueType> extends BaseMutation {
    id: string;
    status?: Status<ValueType>;
    revert?: Revert<ValueType>;
    assign?: Assign<ValueType>;
    clear?: Clear<ValueType>;
    remove?: Remove<ValueType>;
    append?: Append<ValueType>;
    prepend?: Prepend<ValueType>;

    private constructor() { super(); this.id = Elevated.factories.makeObjectId() }

    // implement required methods from Serializable
    static ClassSpec = 'omiglot-live-data.Mutations.OfSequence.Mutation';
    static Factory = Elevated.factories.concrete<Mutation<any>>(Mutation.ClassSpec, () => new Mutation<any>());
    getFactory = () => Mutation.Factory;
    getGlobalId(): number|string|null { return this.id || null; }
    marshal(visitor: Elevated.Visitor<this>): void {
        visitor.begin(this);
        visitor.primitive(this, 'id');
        visitor.scalar(Status.Factory, 'status');
        visitor.scalar(Revert.Factory, 'revert');
        visitor.scalar(Assign.Factory, 'assign');
        visitor.scalar(Clear.Factory, 'clear') ;
        visitor.scalar(Remove.Factory, 'remove') ;
        visitor.scalar(Append.Factory, 'append') ;
        visitor.scalar(Prepend.Factory, 'prepend') ;
        visitor.reference(Mutation.Factory, 'prev');
        visitor.reference(Mutation.Factory, 'next');
        visitor.end(this);
    }

    isClear(): boolean {
        return !!this.clear;
    }

    isSumma(): boolean {
        return !!this.assign || !!this.clear;
    }

    clone(prev: Elevated.Reference<this>): this {
        const result = new Mutation<ValueType>();
        result.status = this.status;
        result.prev = prev;
        result.assign = this.assign;
        result.clear = this.clear;
        result.remove = this.remove;
        result.append = this.append;
        result.prepend = this.prepend;
        return <this>result;
    }
    toString() {
        if(this.status) {
            return `${this.id} SYNC`;
        } else if(this.assign) {
            return `${this.id} ASSIGN ${this.assign.value}`;
        } else if(this.clear) {
            return `${this.id} CLEAR`;
        } else if(this.remove) {
            return `${this.id} REMOVE ${JSON.stringify(Array.from(this.remove.values))}`;
        } else if(this.append) {
            return `${this.id} APPEND ${this.append.values} ${this.append.after ? 'AFTER '+this.append.after : ''}`;
        } else if(this.prepend) {
            return `${this.id} PREPEND ${this.prepend.values} ${this.prepend.before ? 'BEFORE '+this.prepend.before : ''}`;
        }
        return '?';
    }
    stream(fetch: (ref: Elevated.Reference<Mutation<ValueType>>) => Promise<this>): LazyQueue.LazyLoadable<ValueType> {
        const lazyQueue = new LazyQueue.LazyLoadable<ValueType>();
        if(this.status) {
            if(this.prev.isValid()) {
                lazyQueue.fill((yield_, delegate_) =>
                    this.prev.def(fetch)
                    .then(prev => prev.stream(fetch))
                    .then(prevValues => delegate_(prevValues, (value) => true))
                    .then(() => false)
                );
            }
        } else if(this.assign) {
            const assign = this.assign;
            lazyQueue.fill((yield_, delegate_) => {
                for(let item of assign.value) {
                    yield_(item);
                }
                return Promise.resolve(false);
            })
        } else if(this.clear) {
            // nothing to yield                
        } else if(this.remove) {
            const remove = this.remove;
            lazyQueue.fill((yield_, delegate_) => {
                if(this.prev.isValid())
                    return this.prev.def(fetch)
                    .then(prev => prev.stream(fetch))
                    .then((prevValues) => 
                        delegate_(prevValues, prevValue => !remove.values.has(prevValue))
                    )
                    .then(() => false);
                else
                    return Promise.resolve(false);
            });
        } else if(this.append) {
            const append = this.append;
            lazyQueue.fill((yield_, delegate_) => {
                if(this.prev.isValid()) {
                    return this.prev.def(fetch)
                    .then(prev => prev.stream(fetch))
                    .then(async (prevValues) => { 
                        let found = false;
                        for await (let value of prevValues) {
                            if(append.after === undefined) {
                                yield_(value);
                            } else if(value === append.after) {
                                found = true;
                                yield_(value);
                            } else if(!found) {
                                yield_(value);
                            }
                        }
                        return false;
                    });
                }
                return Promise.resolve(false);
            })
            lazyQueue.fill((yield_, delegate_) => {
                for(let value of append.values)
                    yield_(value);
                return Promise.resolve(false);
            });
            lazyQueue.fill((yield_, delegate_) => {
                if(this.prev.isValid()) {
                    return this.prev.def(fetch)
                    .then(prev => prev.stream(fetch))
                    .then(async (prevValues) => { 
                        let found = false;
                        for await (let value of prevValues) {
                            if(append.after === undefined) {
                                // do nothing
                            } else if(!found && value === append.after) {
                                found = true;
                            } else if(found) {
                                yield_(value);
                            }
                        }
                        return false;
                    });
                }
                return Promise.resolve(false);
            })
        } else if(this.prepend) {
            const prepend = this.prepend;
            lazyQueue.fill((yield_, delegate_) => {
                if(this.prev.isValid()) {
                    return this.prev.def(fetch)
                    .then(prev => prev.stream(fetch))
                    .then(async (prevValues) => { 
                        let found = false;
                        for await (let value of prevValues) {
                            if(prepend.before === undefined) {
                                // do nothing
                            } else if(value === prepend.before) {
                                found = true;
                            } else if(prepend.before !== undefined && !found) {
                                yield_(value);
                            }
                        }
                        return false;
                    });
                }
                return Promise.resolve(false);
            })
            lazyQueue.fill((yield_, delegate_) => {
                for(let value of prepend.values)
                    yield_(value);
                return Promise.resolve(false);
            });
            lazyQueue.fill((yield_, delegate_) => {
                let found = false;
                if(this.prev.isValid()) {
                    return this.prev.def(fetch)
                    .then(prev => prev.stream(fetch))
                    .then(async (prevValues) => { 
                        let found = false;
                        for await (let value of prevValues) {
                            if(prepend.before === undefined ) {
                                yield_(value);
                            } else if(!found && value === prepend.before) {
                                found = true;
                                yield_(value);
                            } else if(found) {
                                yield_(value);
                            }
                        }
                        return false;
                    });
                }
                return Promise.resolve(false);
            })
        }
        return lazyQueue;
    }

    dispatch(client: SequenceClient<ValueType>) {
        if(this.assign && this.assign.value) {
            client.assign(this.assign.value);
        } else if(this.clear) {
            client.clear();
        } else if(this.remove) {
            const remove = this.remove;
            client.remove(...Array.from(remove.values));
        } else if(this.append) {
            const append = this.append;
            client.append(append.after, ... append.values);
        } else if(this.prepend) {
            const prepend = this.prepend;
            client.prepend(prepend.before, ... prepend.values);
        } else if(this.revert) {
        }        
    }

    static fromSync<ValueType>(
        prev: Elevated.Reference<Mutation<ValueType>>|Mutation<ValueType>|null,
        status_: Streams.Status
    ) {
        const result = new Mutation<ValueType>();
        result.prev = Elevated.Reference.from(prev);
        result.status = Status.from(status_);
        return result;
    }
    static fromRevert<ValueType>(
        prev: Elevated.Reference<Mutation<ValueType>>|Mutation<ValueType>|null,
    ) {
        const result = new Mutation<ValueType>();
        result.prev = Elevated.Reference.from(prev);
        result.revert = Revert.from({ toMutation: Elevated.Reference.from(prev) });
        return result;
    }
    static fromAssign<ValueType>(
        prev: Elevated.Reference<Mutation<ValueType>>|Mutation<ValueType>|null,
        ...value: ValueType[]
    ) {
        const result = new Mutation<ValueType>();
        result.prev = Elevated.Reference.from(prev);
        result.assign = Assign.from({ value });
        return result;
    }
    static fromClear<ValueType>(
        prev: Elevated.Reference<Mutation<ValueType>>|Mutation<ValueType>|null
    ) {
        const result = new Mutation<ValueType>();
        result.prev = Elevated.Reference.from(prev);
        result.clear = Clear.from({ });
        return result;
    }
    static fromRemove<ValueType>(
        prev: Elevated.Reference<Mutation<ValueType>>|Mutation<ValueType>|null,
        ...values: ValueType[]
    ) {
        const result = new Mutation<ValueType>();
        result.prev = Elevated.Reference.from(prev);
        result.remove = Remove.from({ values: new Set(values) });
        return result;
    }
    static fromAppend<ValueType>(
        prev: Elevated.Reference<Mutation<ValueType>>|Mutation<ValueType>|null,
        after: ValueType|undefined,
        ...values: ValueType[]
    ) {
        const result = new Mutation<ValueType>();
        result.prev = Elevated.Reference.from(prev);
        result.append = Append.from({ after, values });
        return result;
    }
    static fromPrepend<ValueType>(
        prev: Elevated.Reference<Mutation<ValueType>>|Mutation<ValueType>|null,
        before: ValueType|undefined,
        ...values: ValueType[]
    ) {
        const result = new Mutation<ValueType>();
        result.prev = Elevated.Reference.from(prev);
        result.prepend = Prepend.from({ before, values });
        return result;
    }
}

function Optimize<ValueType>(
    mutation: Mutation<ValueType>,
    fetch: (ref: Elevated.Reference<Mutation<ValueType>>) => Promise<Mutation<ValueType>>, 
    insert: (mutation: Mutation<ValueType>) => void, 
    remove: (mutation: Mutation<ValueType>) => void
): Promise<Mutation<ValueType>> {
    if(!!mutation.clear || (!!mutation.assign && mutation.assign.value === undefined)) {
        const replacement = <Mutation<ValueType>>Mutation.fromClear<ValueType>(Elevated.Reference.fromNull())
        insert(replacement);
        return Elevated.Reference.from(mutation).collect((mutation: Mutation<ValueType>) => mutation.prev, fetch, (mutation: Mutation<ValueType>) => false)
        .then(mutations => mutations.forEach((mutation: Mutation<ValueType>) => remove(mutation)))
        .then(() => replacement);
    } else if(!!mutation.assign) {
        const replacement = <Mutation<ValueType>>Mutation.fromAssign<ValueType>(Elevated.Reference.fromNull(), ... mutation.assign.value)
        insert(replacement);
        return Elevated.Reference.from(mutation).collect((mutation: Mutation<ValueType>) => mutation.prev, fetch, (mutation: Mutation<ValueType>) => false)
        .then(mutations => mutations.forEach((mutation: Mutation<ValueType>) => remove(mutation)))
        .then(() => replacement);
    } else if(mutation.prev.isValid()) {
        return mutation.prev.def(fetch)
        .then(prev => {
            if(!!mutation.remove && !!prev.assign) {
                const replacement = <Mutation<ValueType>>Mutation.fromAssign<ValueType>(prev.prev, ... Array.from(prev.assign.value).filter((value: ValueType) => !mutation.remove?.values.has(value)));
                insert(replacement);
                return Elevated.Reference.from(mutation).collect((mutation: Mutation<ValueType>) => mutation.prev, fetch, (mutation: Mutation<ValueType>) => prev.prev.pointsTo(mutation))
                .then(mutations => mutations.forEach((mutation: Mutation<ValueType>) => remove(mutation)))
                .then(() => replacement);
            } else if(!!mutation.remove && !!prev.clear) {
                remove(mutation);
                return prev;
            } else if(!!mutation.append && !!prev.assign) {
                const order = mutation.append.after === undefined
                    ? [ ... prev.assign.value, ... mutation.append.values ]
                    : prev.assign.value.reduce((order: ValueType[], value: ValueType) => {
                        if(value === mutation.append?.after) {
                            return [ ... order, value, ... mutation.append.values ];
                        } else {
                            return [ ... order, value ];
                        }
                    }, []);
                const replacement = <Mutation<ValueType>>Mutation.fromAssign<ValueType>(prev.prev, ... order);
                insert(replacement);
                return Elevated.Reference.from(mutation).collect((mutation: Mutation<ValueType>) => mutation.prev, fetch, (mutation: Mutation<ValueType>) => prev.prev.pointsTo(mutation))
                .then(mutations => mutations.forEach((mutation: Mutation<ValueType>) => remove(mutation)))
                .then(() => replacement);
            } else if(!!mutation.prepend && !!prev.assign) {
                const order = mutation.prepend.before === undefined
                    ? [ ... mutation.prepend.values, ... prev.assign.value ]
                    : prev.assign.value.reduce((order: ValueType[], value: ValueType) => {
                        if(value === mutation.prepend?.before) {
                            return [ ... order, ... mutation.prepend.values, value ];
                        } else {
                            return [ ... order, value ];
                        }
                    }, []);
                const replacement = <Mutation<ValueType>>Mutation.fromAssign<ValueType>(prev.prev, ... order);
                insert(replacement);
                return Elevated.Reference.from(mutation).collect((mutation: Mutation<ValueType>) => mutation.prev, fetch, (mutation: Mutation<ValueType>) => prev.prev.pointsTo(mutation))
                .then(mutations => mutations.forEach((mutation: Mutation<ValueType>) => remove(mutation)))
                .then(() => replacement);
            }
            return mutation;
        })
    }
    return Promise.resolve(mutation);
}

export function Tally<
    ValueType, 
    DomainTypeIn extends Elevated.Domain<ValueType>, 
    ResultType,
    DomainTypeOut extends Elevated.Domain<ResultType>
>(
    name: string,
    stream: SequenceStream<ValueType, DomainTypeIn>,
    clear: () => ResultType,
    add: (accum: ResultType, value_: ValueType) => ResultType,
    remove: (accum: ResultType, value_: ValueType) => ResultType,
    domain: DomainTypeOut
) {
    return {
        persistKey: Stores.Key.fromEmpty(),
        peerId: Elevated.factories.makeObjectId(),
        domain,
        watch(peerId: string) { return new class {
            mutations(observer: rxjs.Observer<OfScalar.Mutation<ResultType>>) {
                let _accum = clear();
                let _mutation: null|OfScalar.Mutation<ResultType> = null;
                // operators must prime the observer!
                observer.next(OfScalar.Mutation.fromClear<ResultType>(null).prime());
                return stream.watch(name).mutations(new class {
                    next(received: Mutation<ValueType>) {
                        let found = false;
                        if(received.assign) {
                            _accum = clear();
                            for(let value of received.assign.value) {
                                const accum_ = add(_accum, value)
                                if(accum_ !== _accum) {
                                    found = true;
                                    _accum = accum_;
                                }
                        }
                        } else if(received.clear) {
                            _accum = clear();
                            found = true;
                        } else if(received.prepend) {
                            for(let value of received.prepend.values) {
                                const accum_ = add(_accum, value)
                                if(accum_ !== _accum) {
                                    found = true;
                                    _accum = accum_;
                                }
                        }
                        } else if(received.append) {
                            for(let value of received.append.values) {
                                const accum_ = add(_accum, value)
                                if(accum_ !== _accum) {
                                    found = true;
                                    _accum = accum_;
                                }
                        }
                        } else if(received.remove) {
                            for(let value of received.remove.values) {
                                const accum_ = remove(_accum, value)
                                if(accum_ !== _accum) {
                                    found = true;
                                    _accum = accum_;
                                }
                            }
                        }
                    if(found) {
                            _mutation = OfScalar.Mutation.fromAssign(_mutation, _accum);
                            observer.next(_mutation)
                        }
                    }
                    complete() { observer.complete() }
                    error(err:any) { observer.error(err) }
                });
            }
        } },
        persistent: () => undefined,
        stateful: () => undefined,
    }
}

export function Map<
    ValueType, 
    DomainTypeIn extends Elevated.Domain<ValueType>, 
    ResultType, 
    DomainTypeOut extends Elevated.Domain<ResultType>
>(
    name: string,
    stream: SequenceStream<ValueType, DomainTypeIn>,
    transform: (value_: undefined|ValueType) => ResultType,
    context: string,
    domain: DomainTypeOut
): SequenceStream<ResultType, DomainTypeOut> {
    let _mutation: Mutation<ResultType>;
    const transform_ = (mutation: Mutation<ValueType>): Mutation<ResultType> => {
        if(mutation.assign) {
            const values_ = Array.from(mutation.assign.value).map(transform);
            _mutation = Mutation.fromAssign(_mutation || null, ...values_);
            _mutation.id = mutation.id;
            return _mutation;
        } else if(mutation.append) {
            const after_ = transform(mutation.append.after);
            const values_ = Array.from(mutation.append.values).map(transform);
            _mutation = Mutation.fromAppend(_mutation || null, after_, ...values_);
            _mutation.id = mutation.id;
            return _mutation;
        } else if(mutation.prepend) {
            const before_ = transform(mutation.prepend.before);
            const values_ = Array.from(mutation.prepend.values).map(transform);
            _mutation = Mutation.fromAppend(_mutation || null, before_, ...values_);
            _mutation.id = mutation.id;
            return _mutation;
        } else if(mutation.remove) {
            const values_ = Array.from(mutation.remove.values).map(transform);
            _mutation = Mutation.fromRemove(_mutation || null, ...values_);
            _mutation.id = mutation.id;
            return _mutation;
        } else if(mutation.clear) {
            _mutation = Mutation.fromClear(_mutation || null);
            _mutation.id = mutation.id;
            return _mutation;
        } else if(mutation.revert) {
            _mutation = Mutation.fromRevert(_mutation || null);
            _mutation.id = mutation.id;
            return _mutation;
        } else if(mutation.status) {
            _mutation = Mutation.fromSync(_mutation || null, mutation.status.status);
            _mutation.id = mutation.id;
            return _mutation;
        } else {
            throw new Error('Malformed Mutation');
        }
    };
    return {
        persistKey: Stores.Key.fromEmpty(),
        peerId: Elevated.factories.makeObjectId(),
        domain, 
        watch(peerId: string) { return new class {
            mutations(observer: rxjs.Observer<Mutation<ResultType>>) {
                // operators must prime the observer!
                observer.next(Mutation.fromClear<ResultType>(null).prime());
                return stream.watch(name).mutations({
                    next: (mutation:Mutation<ValueType>) => observer.next(transform_(mutation)),
                    complete: () => observer.complete(),
                    error: (err: any) => observer.error(err)
                });
            }
        } },
        persistent: () => undefined,
        stateful: () => undefined
    }
}
